import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { aiInterviewsAPI, errMsg } from '../../../lib/aiInterviewApi';
import { jobsAPI } from '../../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { PROCTORING } from './common';

const PROCTOR_HELP = {
  light: 'No ID check. Focus and tab signals only.',
  standard: 'ID + selfie match, liveness, face-count and periodic re-checks.',
  strict: 'Standard plus required full screen.',
};
const Field = ({ label, children, htmlFor }) => (
  <div style={{ display: 'grid', gap: '0.3rem' }}>
    <label className="iv-small" htmlFor={htmlFor} style={{ fontWeight: 700, color: '#002A38' }}>{label}</label>
    {children}
  </div>
);

export default function CreatePlanDialog({ open, onOpenChange, presetJobId }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [f, setF] = useState({ job_id: '', name: 'AI Interview', duration_min: 30, language: 'en', role_level: '', focus: '', proctoring_level: 'standard' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    jobsAPI.list().then(({ data }) => {
      setJobs(data || []);
      setF((x) => ({ ...x, job_id: presetJobId || x.job_id || (data?.[0]?._id || data?.[0]?.id || '') }));
    }).catch(() => toast.error('Could not load jobs'));
  }, [open, presetJobId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await aiInterviewsAPI.createPlan(f.job_id, {
        name: f.name, duration_min: Number(f.duration_min), language: f.language, role_level: f.role_level,
        focus_areas: f.focus.split(',').map((s) => s.trim()).filter(Boolean), proctoring_level: f.proctoring_level,
      });
      toast.success('Plan created. AI is drafting the competencies from the job description.');
      onOpenChange(false);
      navigate(`/ai-interviews/plans/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e, 'Could not create the plan'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="iv max-w-lg" data-testid="create-plan-dialog">
        <DialogHeader>
          <DialogTitle>Create an AI interview plan</DialogTitle>
          <DialogDescription>One plan per role. AI drafts job-related competencies from the JD; each candidate then gets a personalised blueprint from their résumé.</DialogDescription>
        </DialogHeader>
        <div style={{ display: 'grid', gap: '0.8rem' }}>
          <Field label="Job" htmlFor="cp-job">
            <select id="cp-job" className="iv-select" value={f.job_id} onChange={set('job_id')} data-testid="create-plan-job-select">
              {!jobs.length && <option value="">No jobs yet</option>}
              {jobs.map((j) => <option key={j._id || j.id} value={j._id || j.id}>{j.title}</option>)}
            </select>
          </Field>
          <Field label="Plan name" htmlFor="cp-name">
            <input id="cp-name" className="iv-input" value={f.name} onChange={set('name')} data-testid="create-plan-name-input" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
            <Field label="Duration" htmlFor="cp-dur">
              <select id="cp-dur" className="iv-select" value={f.duration_min} onChange={set('duration_min')} data-testid="create-plan-duration-select">
                {[15, 30, 45].map((m) => <option key={m} value={m}>{`${m} minutes`}</option>)}
              </select>
            </Field>
            <Field label="Language" htmlFor="cp-lang">
              <select id="cp-lang" className="iv-select" value={f.language} onChange={set('language')} data-testid="create-plan-language-select">
                <option value="en">English</option><option value="hi">Hindi</option>
              </select>
            </Field>
            <Field label="Role level" htmlFor="cp-level">
              <select id="cp-level" className="iv-select" value={f.role_level} onChange={set('role_level')} data-testid="create-plan-level-select">
                <option value="">Infer from JD</option>
                {['junior', 'mid', 'senior', 'lead'].map((l) => <option key={l} value={l}>{l[0].toUpperCase() + l.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Proctoring" htmlFor="cp-proc">
              <select id="cp-proc" className="iv-select" value={f.proctoring_level} onChange={set('proctoring_level')} data-testid="create-plan-proctoring-select">
                {Object.entries(PROCTORING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <p className="iv-small" style={{ margin: 0 }}>{PROCTOR_HELP[f.proctoring_level]}</p>
          <Field label="Focus areas (optional, comma separated)" htmlFor="cp-focus">
            <input id="cp-focus" className="iv-input" value={f.focus} onChange={set('focus')} placeholder="e.g. system design, stakeholder management" data-testid="create-plan-focus-input" />
          </Field>
        </div>
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => onOpenChange(false)}>Cancel</button>
          <button type="button" className="iv-btn iv-btn-primary" disabled={busy || !f.job_id || !f.name.trim()} onClick={submit} data-testid="create-plan-submit-btn">
            {busy ? 'Creating…' : 'Create plan'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
